import type { ReactNode } from 'react';
import type { ANNOTATION_TYPE as ENTITY_TAGGING_ANNOTATION_TYPE } from '../../entity-tagging/constants/constants';
import type { BoundingBoxT, BrushAnnotationT, PolygonAnnotationT, RectangleAnnotationT, SkeletonTemplateT } from '../../image/labelling';
import { type ANNOTATION_ML_STATE, type ANNOTATION_TYPE as IMAGE_LABELLING_ANNOTATION_TYPE, type CUSTOM_TOOLS, type TOOL } from '../../image/labelling/constants';
import type { ShortcutRegistrationErrorT } from '../../image/labelling/types/types';
import type { ACTION_BY, NOTIFICATION_TYPE } from '../constant/constant';
import type { ReviewActionT } from '../review-action/constants';
import type { ReviewFeaturesT } from '../review-mode-side-panel';
import type { REVIEW_FEATURES } from '../review-mode-side-panel';
import type { TaskModeT } from '../task-state/constants';
import type { TutorialTypeT } from '../tutorial-list/types';
export const TABS = ({
  EDIT: 'Edit',
  REVIEW: 'Review',
  PENDING_ITEMS: 'Pending Items'
} as const);
export const UPDATE_ANNOTATION_TYPE = Object.freeze({
  BASE_ANNOTATION_UPDATE: 'BASE_ANNOTATION_UPDATE',
  ANNOTATION_DATA_UPDATE: 'ANNOTATION_DATA_UPDATE'
});
export type TabsT = (typeof TABS)[keyof typeof TABS];
export type UpdateAnnotationT = (typeof UPDATE_ANNOTATION_TYPE)[keyof typeof UPDATE_ANNOTATION_TYPE];
export type ActionByT = (typeof ACTION_BY)[keyof typeof ACTION_BY];
export type AnnotationTypeT = (typeof IMAGE_LABELLING_ANNOTATION_TYPE)[keyof typeof IMAGE_LABELLING_ANNOTATION_TYPE] | (typeof ENTITY_TAGGING_ANNOTATION_TYPE)[keyof typeof ENTITY_TAGGING_ANNOTATION_TYPE];
export type TimeFormatT = 'SECONDS' | 'MILLI_SECONDS';
export type AnnotationStyleT = {
  strokeColor: string;
  fillColor: string;
  fillOpacity: number;
  strokeWidth: number;
};
export type SkeletonToolConfigT = {
  skeletonTemplates?: SkeletonTemplateT[];
};
export type ToolConfigMapT = {
  [TOOL.SKELETON]?: SkeletonToolConfigT;
};
export type AnnotationLabelT = {
  title: string;
  description?: string;
  style: AnnotationStyleT;
  questionnaire?: Array<QuestionnaireT>;
  max?: number;
  isRequired?: boolean;
  instanceLabelTitle?: string;
  tools?: (keyof typeof TOOL | keyof typeof CUSTOM_TOOLS)[];
  toolConfigs?: ToolConfigMapT;
  maxBoundingBoxes?: number;
};
export type QuestionnaireT = {
  // The `name` field is optional since it's not required for fetching the questionnaire,
  // but it can be helpful for easier identification.
  name?: string;
  questionnaireId: string;
  workspaceId: string;
};
export type QuestionsReviewDataT = {
  [questionId: string]: {
    status: ReviewActionT;
  };
};
export type AnswerT = {
  [questionId: string]: {
    answers: Array<string | File>;
    note?: string;
    noteDesc?: string;
    noteValidator?: string;
    question: string;
    tags?: Array<string>;
  };
};
export type TaskLevelQuestionAnswersT = AnswerT;
export type LabelValidationSuggestionT = {
  id: string;
  questionId: string;
  positionStart: number;
  positionEnd: number;
  message: string;
  suggestion: string;
  textValidationType: 'TEXT_VALIDATION_TYPE_GRAMMAR_AND_SPELLING';
};
export type LabelValidationT = {
  isRequiredLabelAnnotated: boolean;
  reasons: Array<string>;
};
export type LabellingValidationsT = {
  invalidSource: boolean;
  labelValidation: LabelValidationT;
};
export type SAMAnnotationMetadata = Omit<RectangleAnnotationT, 'data' | 'annotationType'> & {
  data: BoundingBoxT;
  type: 'INCLUDE' | 'EXCLUDE';
};
export type AutoDetectAnnotationMetadata = {
  annotationLabel: AnnotationLabelT;
  textPrompt: string;
};
export type AnnotationUnderUserReviewT = {
  type: 'BOUNDING_BOX';
  annotationMetaData: SAMAnnotationMetadata[];
  annotationData?: BrushAnnotationT['data'][];
  frameIndex?: number;
  automationRequestId?: string;
} | {
  type: 'POINT';
  annotationMetaData: SAMAnnotationMetadata[];
  annotationData?: BrushAnnotationT['data'][];
  frameIndex?: number;
  automationRequestId?: string;
} | {
  type: 'POLYGON';
  annotationMetaData: SAMAnnotationMetadata[];
  annotationData?: PolygonAnnotationT['data'][];
  frameIndex?: number;
  automationRequestId?: string;
} | {
  type: 'BOUNDING_BOX_POLYGON';
  annotationMetaData: SAMAnnotationMetadata[];
  annotationData?: PolygonAnnotationT['data'][];
  frameIndex?: number;
  automationRequestId?: string;
} | {
  type: 'AUTO_DETECT';
  annotationMetaData: AutoDetectAnnotationMetadata[];
  annotationData?: BrushAnnotationT['data'][];
  frameIndex?: number;
  automationRequestId?: string;
} | {
  type: 'AUTO_DETECT_POLYGON';
  annotationMetaData: AutoDetectAnnotationMetadata[];
  annotationData?: PolygonAnnotationT['data'][];
  frameIndex?: number;
  automationRequestId?: string;
};
export interface AnnotationUnderUserReviewMapT {
  [key: string]: AnnotationUnderUserReviewT;
}
export type GenericAnnotationT = {
  annotationLabel: AnnotationLabelT;
  metadata?: {
    answers?: AnswerT;
    consensusInfo?: {
      annotator?: number;
    };
  };
  annotationType: string;
  hidden?: boolean;
  locked?: boolean;
  id: string;
};
export type GenericAnnotationReviewDataT = {
  annotationId: string;
  status: ReviewActionT;
  questions?: QuestionsReviewDataT;
  note?: string;
};
export type GroupedAnnotationsT = {
  annotationList: Array<{
    id: string;
  } & GenericAnnotationT>;
  label: AnnotationLabelT;
};
export type GetAnnotationMetaDetailsArgsT = {
  annotationId: string;
  shouldDisplayAllAnnotations?: boolean;
};
export type ComponentT = ReactNode | string;
export type GetAnnotationMetaDetailsReturnT = ({
  parent: ComponentT;
} | {
  icon?: ComponentT;
  description?: ComponentT;
  details?: ComponentT;
}) & {
  config?: {
    disableDelete?: boolean;
    disableSelect?: boolean;
    disableHide?: boolean;
  };
};
export type GetAnnotationMetaDetailsT = (a: GetAnnotationMetaDetailsArgsT) => GetAnnotationMetaDetailsReturnT | undefined;
export type AnnotationMLDataT = {
  state: (typeof ANNOTATION_ML_STATE)[keyof typeof ANNOTATION_ML_STATE];
};
export const STORE_TYPE = ({
  ML_ANNOTATION_STORE: 'ML_ANNOTATION_STORE',
  ANNOTATION_STORE: 'ANNOTATION_STORE',
  CONSENSUS_ANNOTATION_STORE: 'CONSENSUS_ANNOTATION_STORE'
} as const);
export type StoreTypeT = keyof typeof STORE_TYPE;
export type OptionsT = {
  showAllAnnotations?: {
    enabled: boolean;
  };
};
export type FrameRangeT = {
  startFrameIndex: number;
  endFrameIndex: number;
};
export type ShowNotificationT = (notificationType: (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE], message: string) => void;
export type ControlTypesT = {
  divider: boolean;
  element: JSX.Element;
  id: string;
};
export type AlignmentT = {
  top?: string;
  bottom?: string;
  right?: string;
  left?: string;
};
export type TutorialStepT = {
  id: string;
  version: number;
  getTarget: () => Element | null;
  render: () => ReactNode;
  isRoot?: boolean;
  disableNext?: boolean;
  nextId?: string;
  isReplay?: boolean;
  onNext?: (nextId: string) => void;
};
export type TutorialListItemT = {
  name: string;
  section: TutorialTypeT;
  id: string;
  isTutorialReady?: () => {
    state: true;
  } | {
    state: false;
    reason: string;
  };
};
export type TutorialServiceT = {
  onTutorialReplay: (rootNodeId: string, section: TutorialListItemT['section'], name: TutorialListItemT['name'], callback: () => void, isTutorialReady?: TutorialListItemT['isTutorialReady']) => () => void;
  addStep(step: TutorialStepT, rootId: string): void;
  next(stepId: string): void;
  hideStep(stepId: string): void;
  showStep(stepId: string): void;
  triggerReplay(rootStepId: string): void;
  tutorialList: Array<TutorialListItemT>;
  currentActiveTutorial: string | null;
};
export const RELATIONSHIP_TYPES = ({
  childToParent: 'ChildToParent',
  parentToChild: 'ParentToChild',
  unset: 'Unset'
} as const);
export type RelationshipStateT = {
  selectedAnnotationId?: string;
  relationshipType: typeof RELATIONSHIP_TYPES.unset;
} | {
  selectedAnnotationId: string;
  relationshipType: typeof RELATIONSHIP_TYPES.childToParent;
  selectedParentId: string | undefined;
} | {
  selectedAnnotationId: string;
  relationshipType: typeof RELATIONSHIP_TYPES.parentToChild;
  selectedChildrenIds: Array<string>;
};
export type BaseOutputLabelledDataT = {
  version: string;
  metadata?: {
    invalidSource: boolean;
    mediaPlaybackTracking?: boolean[];
  } | null;
};
export type BaseAnnotationT = {
  annotationLabel: AnnotationLabelT;
  metadata?: {
    answers?: AnswerT;
    originalId?: string;
    consensusInfo?: {
      annotator?: number;
    };
  };
  children?: Array<string>;
  hidden?: boolean;
  locked?: boolean;
};
type MetaDataT = {
  invalidSource: boolean;
};
export type FeedbackQuestionnaireConfig = {
  questionnaireId: string;
  workspaceId: string;
};
export type ReviewFeatureDataValueT = {
  feedbackQuestionnaire?: FeedbackQuestionnaireConfig;
};
export type ReviewFeatureDataT = { [key in (typeof REVIEW_FEATURES)[keyof typeof REVIEW_FEATURES]]?: ReviewFeatureDataValueT };
export type TaskStateT = {
  taskModes: TaskModeT[];
  readOnly: boolean;
  disableActions?: boolean;
  reviewModeVersion?: number;
  reviewFeatureConfig?: ReviewFeaturesT;
  reviewFeatureData?: ReviewFeatureDataT;
};
export type BaseInputDataT = {
  taskState: TaskStateT;
  metadata?: MetaDataT | null;
};
export type RangeSpanT = {
  start: number;
  end: number;
};
export type InputFormatT = 'frames' | 'time';
export type ReviewOptionsVisibilityT = {
  enableAcceptAll?: boolean;
  showAllAnnotations?: boolean;
  enableContinuousPlayback?: boolean;
  showListViewMode?: boolean;
};
export type ConfigT = {
  useComposableQuestionnaire?: boolean;
  reviewOptionsVisibility?: ReviewOptionsVisibilityT;
};
export type DynamicAnnotationConfigT = {
  id: string;
  annotation: GenericAnnotationT;
  isValid: boolean;
  frameStart: number;
  frameEnd: number;
  savedToStore: boolean;
};
export type GetSignedUrlT = (referenceUri?: string) => Promise<string | undefined>;
export type RegisterHotkeyActionT = {
  // Supported keyboard and custom events used by the shortcut system
  type?: 'keydown' | 'keyup' | 'visibilitychange' | 'longpress' | 'toggle';
  keys: string[];
  onAction: (e: KeyboardEvent) => void;
  allowInEditable?: boolean;
};
export type RegisterHotkeysArgsT = RegisterHotkeyActionT[];
export type RegisterHotkeysResultT = {
  errors: ShortcutRegistrationErrorT[];
  removeHotKeys: () => void;
};
export type OnMediaLoadingProps = {
  isMediaElementLoading: boolean;
  isRefreshingSource: boolean;
} | {
  isMediaElementLoading: boolean;
} | {
  isRefreshingSource: boolean;
};
export type onMediaSrcExpiryErrorPropsT = {
  mediaError: MediaError | Error;
  skipSourceValidation: boolean;
};